import { cx } from '@emotion/css';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import { Content, Media as MediaSDS } from '@snapchat/snap-design-system-marketing';
import type { CSSProperties, FC } from 'react';

import { CallToAction } from '../../../../components/CallToAction';
import type { ContentfulIdVariable } from '../../../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../../../hooks/useContentfulQuery';
import { useParseMediaContainer } from '../../../../hooks/useParseMediaContainer';
import { renderRichTextMultiLineWithMarkings } from '../../../../utils/renderText/renderRichText';
import { getContrastColor } from '../../helpers/getContrastColor';
import {
  arDownloadLinkCardCss,
  contentWithBgImageCss,
  createCustomButtonStylesCss,
} from './ArDownloadLinkCard.styles';
import type { ArDownloadLinkCardQuery } from './arDownloadLinkCardQuery';
import { query } from './arDownloadLinkCardQuery';

type ArDownloadLinkCardProps = {
  id: string;
};

export const ArDownloadLinkCard: FC<ArDownloadLinkCardProps> = props => {
  const { data } = useContentfulQuery<ArDownloadLinkCardQuery, ContentfulIdVariable>(query, {
    variables: { id: props.id },
  });
  const { getImageSources } = useContentfulImages();

  const {
    title: titleRichText,
    body: bodyRichText,
    titleAlignment,
    bodyAlignment,
    backgroundImage,
    backgroundColor,
    ctaColor,
    icon,
    callToActionsCollection,
    flex,
  } = data?.arDownloadLinkCard || {};

  const {
    media: { imageSource: backgroundImageSource },
  } = useParseMediaContainer(backgroundImage);

  const {
    media: { imageSource: iconImageSource, imageAltText: iconAltText },
  } = useParseMediaContainer(icon);

  if (!data?.arDownloadLinkCard) {
    return null;
  }

  const title = renderRichTextMultiLineWithMarkings(titleRichText);
  const body = renderRichTextMultiLineWithMarkings(bodyRichText);

  const callsToAction = callToActionsCollection?.items?.map(item => (
    <CallToAction key={item.sys.id} {...item} />
  ));

  const asset = iconImageSource ? (
    <MediaSDS imgSrcs={getImageSources(iconImageSource)} altText={iconAltText} />
  ) : undefined;

  const customButtonCss = ctaColor
    ? createCustomButtonStylesCss(ctaColor, getContrastColor(ctaColor))
    : undefined;
  const bgColor = backgroundColor || '#000000FF';
  // Only calculate text color if we're actually using a background color (not image)
  const shouldSetTextColor = !backgroundImageSource || backgroundColor;
  const textColor = shouldSetTextColor ? getContrastColor(bgColor) : undefined;

  return (
    <Content
      title={title}
      body={body}
      bodyAlignment={bodyAlignment}
      titleAlignment={titleAlignment}
      asset={asset}
      callsToAction={callsToAction}
      className={cx(arDownloadLinkCardCss, customButtonCss, {
        [contentWithBgImageCss]: !!backgroundImageSource,
      })}
      style={
        {
          ...(backgroundImageSource && {
            backgroundImage: `url("${backgroundImageSource}")`,
          }),
          ...(!backgroundImageSource && {
            backgroundColor: bgColor,
          }),
          ...(flex && {
            flex,
          }),
          ...(textColor && {
            '--custom-text-color': textColor,
          }),
        } as CSSProperties
      }
    />
  );
};
